import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import '../css/ChatBot.css';
import Logo from '../images/logo.webp';

const CHATBOT_WEBHOOK_URL =
  process.env.REACT_APP_CHATBOT_WEBHOOK_URL ||
  '/webhook/interactive-chat';
const CHATBOT_JWT_TOKEN =
  process.env.REACT_APP_CHATBOT_JWT_TOKEN || process.env.REACT_APP_JWT_TOKEN || '';

const WELCOME_MESSAGE =
  "Welcome to AI City. I can help with AI solutions, integrations, and delivery questions. How can I help today?";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ text: WELCOME_MESSAGE, sender: 'bot' }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [collected, setCollected] = useState({});
  const messagesEndRef = useRef(null);
  const [sessionId] = useState(() => {
    const savedSessionId = localStorage.getItem('chatbotSessionId');
    if (savedSessionId) {
      return savedSessionId;
    }

    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('chatbotSessionId', newSessionId);
    return newSessionId;
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReset = () => {
    localStorage.removeItem('chatbotSessionId');
    setMessages([{ text: WELCOME_MESSAGE, sender: 'bot' }]);
    setInputValue('');
    setCollected({});
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const userMessage = inputValue.trim();
    setMessages((prev) => [...prev, { text: userMessage, sender: 'user' }]);
    setInputValue('');
    setIsLoading(true);

    try {
      const conversationHistory = messages
        .filter((message) => message.sender === 'user' || message.sender === 'bot')
        .map((message) => ({
          sender: message.sender,
          text: message.text,
        }));

      const headers = {
        'Content-Type': 'application/json',
      };

      if (CHATBOT_JWT_TOKEN) {
        headers.Authorization = `Bearer ${CHATBOT_JWT_TOKEN}`;
      }

      const response = await fetch(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId,
          message: userMessage,
          conversationHistory,
          collected,
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed with ${response.status}`);
      }

      const data = await response.json();
      const candidateReplies = [
        data?.reply,
        data?.message,
        data?.output,
        data?.text,
        data?.response,
        data?.data?.reply,
        data?.data?.message,
        data?.data?.output,
        data?.data?.text,
        Array.isArray(data?.choices) ? data.choices[0]?.message?.content : undefined,
      ];

      const reply = candidateReplies
        .find((value) => typeof value === 'string' && value.trim().length > 0)
        ?.trim();

      if (!reply) {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'bot',
            text: 'Webhook reached successfully, but it returned an empty "reply" field. Please check the n8n response mapping.',
          },
        ]);
        return;
      }

      setMessages((prev) => [...prev, { text: reply, sender: 'bot' }]);
      if (data.collected) setCollected(data.collected);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: 'I could not reach the AI service right now. Please try again in a moment.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chatbot-container">
      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-brand">
              <img src={Logo} alt="AI City logo" className="chatbot-logo" />
              <div>
                <h4>AI City Assistant</h4>
                <p>Live support</p>
              </div>
            </div>

            <button
              type="button"
              className="chatbot-reset"
              onClick={handleReset}
              title="Reset conversation"
              aria-label="Reset conversation"
            >
              ↺
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((message, index) => (
              <div key={`${message.sender}-${index}`} className={`message ${message.sender}`}>
                {message.sender === 'bot' ? (
                  <ReactMarkdown>{message.text}</ReactMarkdown>
                ) : (
                  message.text
                )}
              </div>
            ))}

            {isLoading && (
              <div className="message bot loading" aria-live="polite">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              placeholder="Ask about AI City solutions..."
              disabled={isLoading}
              aria-label="Message input"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              aria-label="Send message"
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
